import { useCallback } from 'react';

import { LoadingScreen } from '#modules/ui/LoadingScreen';

import { useDynamicAssessmentHost } from '../../hooks/useDynamicAssessmentHost';
import type { DynamicNodeProgress } from '../../types/dynamicAssessment';
import {
  DynamicAssessmentFlow,
  type DynamicAssessmentDoneReason,
  type DynamicAssessmentResult,
} from '../DynamicAssessmentFlow/DynamicAssessmentFlow';

type DynamicAssessmentScreenProps = {
  /**
   * Forwarded verbatim to `DynamicAssessmentFlow`'s own `onProgress` — see its
   * doc comment for the contract. Optional and absent today: the onboarding
   * app's own `/step/dynamic-assessment` route draws its progress from
   * `rulesEngine`'s `onboardingStepProgress`, not from this. Exists so a host
   * that embeds `DynamicAssessmentScreen` (this module's other public,
   * flag-gated entry point, alongside `DynamicAssessmentFlow`) isn't stuck
   * with no way to ever receive it.
   */
  onProgress?: (progress: DynamicNodeProgress | null) => void;
};

/**
 * The dynamic-assessment step — in onboarding, `/step/dynamic-assessment`, the
 * `onb_use_dynamic_assessment` TEST arm only. It gates on the host's flag state and
 * translates the flow's terminal outcomes into host callbacks; the host decides
 * what any of them mean for navigation.
 *
 * Everything it needs arrives through `DynamicAssessmentProvider`'s `host` rather than
 * `@/modules/rulesEngine`, so this module never pulls the rules engine (and with it
 * analytics → api → chat) into consumers that don't have one. The onboarding app's
 * implementation is `useOnboardingSurveyHost` in `@/modules/rulesEngine`.
 *
 * In the onboarding app specifically: the resolver routes the whole assessment region
 * here (health conditions → goals → AOM screeners → US state → lab preference →
 * diabetes decision → pre-diabetes → phq/gad, served as server-driven flows), and a
 * clean finish advances to `/step/assigned-screeners`, where the legacy flow mops up
 * any screener dynamic assessment doesn't support yet — see `dynamicSurveyNextStep`.
 * Control (or a failed flag read, which fails open to control) never reaches this
 * route.
 *
 * The one subtlety worth preserving: a crisis outcome captured before a LATER,
 * unrelated failure (e.g. the next pass's `/flows/` read throwing after
 * phq_gad_v1's crisis terminal already came back — phq_gad_v1 is last, so the
 * likeliest spot) must NOT be lost to the error branch. An errored abandon skips
 * the assessment region, so the defer-until-exhaustion consumption never runs; hence
 * `onCrisisImmediate` rather than `onCrisisDeferred` on that path.
 */
export function DynamicAssessmentScreen({ onProgress }: DynamicAssessmentScreenProps = {}) {
  const {
    userUuid,
    isEnabled,
    hasErrored,
    onCompleted,
    onCrisisDeferred,
    onCrisisImmediate,
    onFailed,
  } = useDynamicAssessmentHost();

  const handleDynamicDone = useCallback(
    (reason: DynamicAssessmentDoneReason, result?: DynamicAssessmentResult) => {
      // `DynamicAssessmentFlow` passes `crisisProgramUrns` regardless of reason,
      // so a real crisis outcome can arrive alongside `reason === 'error'`.
      // Handle it before the error branch's early return, which would otherwise
      // discard it and route the member to the normal program region without the
      // crisis-line routing they qualified for.
      const crisisUrns = result?.crisisProgramUrns;
      if (reason === 'error') {
        if (crisisUrns && crisisUrns.length > 0) {
          // The errored abandon skips the assessment region, so the host's
          // defer-until-exhaustion consumption never runs — enter the crisis
          // program now so the host routes there instead of the normal program
          // region.
          onCrisisImmediate(crisisUrns);
          return;
        }
        // All-or-nothing: hand the failure to the host, which abandons the assessment
        // region and navigates this screen away (no legacy fallback).
        onFailed();
        return;
      }
      // Clean finish: a crisis outcome from phq_gad_v1 is deferred until assessment
      // exhaustion (the legacy screeners + state-compliance still run first).
      if (crisisUrns && crisisUrns.length > 0) {
        onCrisisDeferred(crisisUrns);
      }
      // Hand over which flows actually completed so the host can skip a standalone
      // pre-diabetes / phq-gad screen ONLY when its dynamic flow ran — an
      // unpublished/abandoned flow isn't listed, so its standalone screener still
      // runs rather than being silently dropped. The host owns advancing.
      onCompleted(result?.completedFlowKeys ?? []);
    },
    [onCrisisImmediate, onFailed, onCrisisDeferred, onCompleted]
  );

  // An errored dynamic pass abandons the assessment region: the host is navigating
  // this screen away. Hold a spinner for the render between the latch and the
  // redirect.
  if (hasErrored) {
    return <LoadingScreen />;
  }

  // Until the flag resolves (`undefined`), hold the spinner. If it resolves to
  // anything but the test arm (control, or an errored read that fails open to
  // control), this route shouldn't have been reached — hold the spinner and let
  // the host's forward-redirect send the user to their real route.
  if (isEnabled !== true) {
    return <LoadingScreen />;
  }

  return (
    <DynamicAssessmentFlow onDone={handleDynamicDone} userUuid={userUuid} onProgress={onProgress} />
  );
}
